import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import AppTheme from "../../../theme/AppTheme";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, Card, useMediaQuery, useTheme, Badge, Switch, FormControlLabel, Typography } from "@mui/material";
import TabsComponent from "../../../components/Tabs/TabsComponent";
import Orders from "../Orders";
import Return from "../../Return/Return";
import Invoice from "../Fragments/Invoice/Invoice";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { setActiveKey } from "../../../Redux/Slices/tabSlice";
import type { tabStep } from "../../../Redux/Slices/tabSlice";
import New from "../Fragments/New/New";
import Ready from "../Fragments/Ready/Ready";
import Delivered from "../Fragments/Delivered/Delivered";
import Preparing from "../Fragments/Preparing/Preparing";
import { useParams } from "react-router-dom";
import {
  getNewOrders,
  getPreparingOrders,
  getReadyOrders,
  getDeliveredOrders,
} from "../../../service/order/orderService";
import { notifyError } from "../../../components/ToastContainer/Toast/Toast";
import StoreHeader from "../../../components/StoreHeader/StoreHeader";

// Context for sharing order counts and auto-refresh state between tabs
interface OrderCountsContextType {
  counts: {
    new: number;
    preparing: number;
    ready: number;
    delivered: number;
  };
  updateCount: (
    tab: keyof OrderCountsContextType["counts"],
    count: number
  ) => void;
  incrementCount: (
    tab: keyof OrderCountsContextType["counts"]
  ) => void;
  decrementCount: (
    tab: keyof OrderCountsContextType["counts"]
  ) => void;
  moveOrderCount: (
    fromTab: keyof OrderCountsContextType["counts"],
    toTab: keyof OrderCountsContextType["counts"]
  ) => void;
  countsLoaded: boolean;
  setCountsLoaded: (loaded: boolean) => void;
  isAutoRefreshEnabled: boolean;
  setIsAutoRefreshEnabled: (enabled: boolean) => void;
  triggerRefresh: () => void;
  refreshCallbacksRef: React.MutableRefObject<{
    newOrder?: () => void;
    preparing?: () => void;
    ready?: () => void;
  }>;
}

const OrderCountsContext = createContext<OrderCountsContextType | undefined>(
  undefined
);

export { OrderCountsContext };

export const useOrderCounts = () => {
  const context = useContext(OrderCountsContext);
  if (!context) {
    throw new Error("useOrderCounts must be used within OrderCountsProvider");
  }
  return context;
};

const formSteps = [
  "orders",
  "invoice",
  "return",

  "newOrder",
  "preparing",
  "ready",
  // "delivered",
] as const;

const TabName = [
  "Orders",
  "Invoice",
  "Return",

  "New",
  "Preparing",
  "Ready",
  // "Delivered",
] as const;

const Forms: Record<tabStep, React.ComponentType<any>> = {
  orders: Orders,
  invoice: Invoice,
  return: Return,

  newOrder: New,
  preparing: Preparing,
  ready: Ready,
  // delivered: Delivered,
};

export default function InvoiceTabs(props: { disableCustomTheme?: boolean }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { store_id } = useParams();

  // State for order counts
  const [orderCounts, setOrderCounts] = useState({
    new: 0,
    preparing: 0,
    ready: 0,
    delivered: 0,
  });
  
  // Flag to track if initial counts have been loaded
  const [countsLoaded, setCountsLoaded] = useState(false);

  // Auto-refresh state - persisted per store
  const autoRefreshKey = `autoRefresh_orders_${store_id}`;
  const [isAutoRefreshEnabled, setIsAutoRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem(autoRefreshKey);
    return saved === 'true';
  });

  // Get activeKey and categoryId early
  const dispatch = useAppDispatch();
  const activeKey = useAppSelector((state) => state.tab.activekey);
  const categoryIdFromStorage = localStorage.getItem("selectedStoreCategoryName");
  // Tabs are driven by store category. If category is F&B, show F&B order-flow tabs.
  const categoryId = categoryIdFromStorage;

  // Ref to store refresh callback functions from child components
  const refreshCallbacksRef = useRef<{
    newOrder?: () => void;
    preparing?: () => void;
    ready?: () => void;
  }>({});

  // Each tab renders inside a scrollable MUI Card (overflowY: auto). Keep refs so we can reset
  // scroll position to top whenever the active tab changes (manual click or programmatic navigation).
  const tabScrollContainerRef = useRef<Record<string, HTMLDivElement | null>>({});
  const tabsRootRef = useRef<HTMLDivElement | null>(null);

  // Prevent browser scroll restoration from re-applying previous positions.
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = prev;
      };
    }
    return;
  }, []);

  const updateCount = (tab: keyof typeof orderCounts, count: number) => {
    setOrderCounts((prev) => ({
      ...prev,
      [tab]: count,
    }));
  };

  const incrementCount = (tab: keyof typeof orderCounts) => {
    setOrderCounts((prev) => ({
      ...prev,
      [tab]: prev[tab] + 1,
    }));
  };

  const decrementCount = (tab: keyof typeof orderCounts) => {
    setOrderCounts((prev) => ({
      ...prev,
      [tab]: Math.max(0, prev[tab] - 1),
    }));
  };

  const moveOrderCount = (fromTab: keyof typeof orderCounts, toTab: keyof typeof orderCounts) => {
    setOrderCounts((prev) => ({
      ...prev,
      [fromTab]: Math.max(0, prev[fromTab] - 1),
      [toTab]: prev[toTab] + 1,
    }));
  };

  // Handle auto-refresh toggle change
  const handleAutoRefreshToggle = (checked: boolean) => {
    setIsAutoRefreshEnabled(checked);
    localStorage.setItem(autoRefreshKey, checked.toString());
    
    // If enabling, immediately trigger refresh for active tab
    if (checked && activeKey) {
      triggerRefresh();
    }
  };

  // Trigger refresh for the currently active tab
  const triggerRefresh = useCallback(() => {
    if (activeKey === 'newOrder' && refreshCallbacksRef.current.newOrder) {
      refreshCallbacksRef.current.newOrder();
    } else if (activeKey === 'preparing' && refreshCallbacksRef.current.preparing) {
      refreshCallbacksRef.current.preparing();
    } else if (activeKey === 'ready' && refreshCallbacksRef.current.ready) {
      refreshCallbacksRef.current.ready();
    }
  }, [activeKey]);

  // Auto-refresh effect: poll API every 30 seconds when enabled
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (isAutoRefreshEnabled && (activeKey === 'newOrder' || activeKey === 'preparing' || activeKey === 'ready')) {
      // Set up interval to refresh every 30 seconds
      intervalId = setInterval(() => {
        triggerRefresh();
      }, 30000); // 30 seconds
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAutoRefreshEnabled, activeKey, triggerRefresh]);

  const contextValue = {
    counts: orderCounts,
    updateCount,
    incrementCount,
    decrementCount,
    moveOrderCount,
    countsLoaded,
    setCountsLoaded,
    isAutoRefreshEnabled,
    setIsAutoRefreshEnabled: handleAutoRefreshToggle,
    triggerRefresh,
    refreshCallbacksRef, // Expose ref so children can register their refresh functions
  };

  const handleTabChange = (key: string) => {
    if (formSteps.includes(key as tabStep)) {
      dispatch(setActiveKey(key as tabStep));
    }
  };

  // Always reset scroll position when switching tabs (manual click or programmatic navigation).
  // At higher zoom levels (e.g. 125%), the scrollable element can be the window, Ant Tabs' content
  // holder, or the per-tab Card (overflowY: auto). Reset all of them.
  useEffect(() => {
    if (!activeKey) return;

    const resetScrollTopEverywhere = () => {
      // 1) Window / document scroll
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      // Fallbacks for some browsers/layouts
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      // 2) Ant Tabs scroll containers (if present)
      const tabsHolders = Array.from(
        document.querySelectorAll<HTMLElement>(".ant-tabs-content-holder, .ant-tabs-content"),
      );
      tabsHolders.forEach((el) => {
        el.scrollTop = 0;
      });

      // 3) Per-tab Card scroll container
      const el = tabScrollContainerRef.current[String(activeKey)];
      if (el) el.scrollTop = 0;

      // 3b) Also reset scroll on any scrollable ancestors of the active tab container.
      // This catches app-level layout scrollers that sit outside InvoiceTabs' root.
      if (el) {
        let node: HTMLElement | null = el;
        while (node) {
          const parent = node.parentElement;
          if (!parent) break;
          if (parent.scrollHeight > parent.clientHeight + 1) {
            const s = window.getComputedStyle(parent);
            const oy = s.overflowY;
            if (oy === "auto" || oy === "scroll") parent.scrollTop = 0;
          }
          node = parent;
        }
      }

      // 4) Any other scrollable container inside this module (covers zoom/responsive layouts)
      const root = tabsRootRef.current;
      if (root) {
        const all = Array.from(root.querySelectorAll<HTMLElement>("*"));
        for (const node of all) {
          // Only touch elements that can actually scroll.
          if (node.scrollHeight <= node.clientHeight + 1) continue;
          const style = window.getComputedStyle(node);
          const overflowY = style.overflowY;
          if (overflowY !== "auto" && overflowY !== "scroll") continue;
          node.scrollTop = 0;
        }
      }
    };

    // Run multiple passes to cover: tab DOM mount, data fetch render, and zoom/layout shifts.
    const timeouts: number[] = [];
    const rafs: number[] = [];
    const run = () => resetScrollTopEverywhere();

    rafs.push(window.requestAnimationFrame(run));
    rafs.push(window.requestAnimationFrame(run));
    timeouts.push(window.setTimeout(run, 0));
    timeouts.push(window.setTimeout(run, 50));
    timeouts.push(window.setTimeout(run, 200));
    timeouts.push(window.setTimeout(run, 500));
    timeouts.push(window.setTimeout(run, 1000));

    return () => {
      rafs.forEach((id) => window.cancelAnimationFrame(id));
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, [activeKey]);

  const visibleFormSteps =
    categoryId === "F&B"
      ? (["newOrder", "preparing", "ready", "delivered"] as const)
      : (["orders", "invoice", "return"] as const);

  const createTabLabel = (baseName: string, count: number) => (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "center" }}>
      <span>{baseName}</span>
      {count > 0 && (
        <Box
          sx={{
            backgroundColor: "black",
            color: "white",
            fontSize: "0.75rem",
            fontWeight: "bold",
            minWidth: "20px",
            height: "20px",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: 1,
          }}
        >
          {count}
        </Box>
      )}
    </Box>
  );

  const visibleTabNames =
    categoryId === "F&B"
      ? ([
          createTabLabel("New", orderCounts.new),
          createTabLabel("Preparing", orderCounts.preparing),
          createTabLabel("In Progress", orderCounts.ready),
          // createTabLabel("Delivered", orderCounts.delivered),
        ] as const)
      : (["Orders", "Invoice", "Return"] as const);

  React.useEffect(() => {
    const defaultTab: tabStep = categoryId === "F&B" ? "newOrder" : "orders";

    const steps = [...visibleFormSteps] as tabStep[];

    // Check for hash in URL to navigate to specific tab (e.g., #newOrder, #preparing)
    const hash = window.location.hash.replace('#', '');
    if (hash && steps.includes(hash as tabStep)) {
      // Always set the tab from hash, even if activeKey is already set
      // This ensures navigation from other pages properly updates the UI
      dispatch(setActiveKey(hash as tabStep));
      // Clear the hash after setting the tab
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } else if (!activeKey || !steps.includes(activeKey)) {
      // Only set default if activeKey is not in valid steps
      dispatch(setActiveKey(defaultTab));
    }
  }, [categoryId, dispatch, visibleFormSteps]); // Removed activeKey from dependencies to allow hash to override

  const FALLBACK_RESPONSE = { data: { data: { total: 0 } } };

  const withFallback = <T,>(
    promise: Promise<T>
  ): Promise<T | typeof FALLBACK_RESPONSE> =>
    promise.catch(() => FALLBACK_RESPONSE);

  React.useEffect(() => {
    if (categoryId === "F&B") {
      fetchAllTabCounts();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, activeKey]);

  const fetchAllTabCounts = async () => {
    try {
      const [newRes, preparingRes, readyRes, deliveredRes] = await Promise.all([
        withFallback(getNewOrders(1, store_id, "pending")),
        withFallback(getPreparingOrders(1, store_id, "billed")),
        withFallback(getReadyOrders(1, store_id, undefined)),
        withFallback(getDeliveredOrders(1, store_id, "delivered")),
      ]);

      updateCount("new", newRes?.data?.data?.total || 0);
      updateCount("preparing", preparingRes?.data?.data?.total || 0);
      updateCount("ready", readyRes?.data?.data?.total || 0);
      updateCount("delivered", deliveredRes?.data?.data?.total || 0);

      setCountsLoaded(true);
    } catch (error) {
      notifyError("Failed to fetch order counts");
    }
  };

  const tabsData = visibleFormSteps.map((step, i) => ({
    label: visibleTabNames[i],
    key: step,
    children: (
      <Card
        ref={(el) => {
          tabScrollContainerRef.current[String(step)] = el;
        }}
        sx={{
          position: "relative",
          backgroundColor: "white",
          minHeight: "600px",
          maxHeight: "calc(100vh - 125px)",
          borderRadius: "8px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {step === activeKey ? React.createElement(Forms[step], {}) : null}
      </Card>
    ),
  }));
  
  // Show auto-refresh toggle for F&B category on New/Preparing/Ready tabs
  const showAutoRefresh =
    categoryId === "F&B" &&
    (activeKey === "newOrder" || activeKey === "preparing" || activeKey === "ready");

  return (
    <OrderCountsContext.Provider value={contextValue}>
      <AppTheme {...props}>
        <CssBaseline enableColorScheme />
        <Box ref={tabsRootRef} sx={{ width: "100%", height: "100%", pt: isMobile ? 5 : 0 }}>
          <StoreHeader />
          {/* Auto-refresh toggle - Visible for New/Preparing/Ready tabs in F&B category */}
          {showAutoRefresh && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, px: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isAutoRefreshEnabled}
                    onChange={(e) => handleAutoRefreshToggle(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.primary' }}>
                    Auto Refresh
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Box>
          )}
          <TabsComponent
            tabsData={tabsData}
            activeKey={activeKey}
            onChange={handleTabChange}
            tabPosition="top"
          />
        </Box>
      </AppTheme>
    </OrderCountsContext.Provider>
  );
}
